import Vue from 'vue';
import get from "lodash/get";
import store from '@/store';
import i18n from '@/language/i18n';

function getWindowsByType(state, type) {
  return state.windows.find(windowGroup => windowGroup.type === type)?.windows;
}

function getWindow(state, type, id) {
  return getWindowsByType(state, type)?.find(window => window.id == id);
}

function checkDirtyWindows(state) {
  const dirtyWindowsCount = state.windows.flatMap(
    ({ type, windows }) => windows.map(({ id, data: { isDirty } }) =>
      ({ type, id, isDirty }))
  ).filter(window => window.isDirty).length;

  if (dirtyWindowsCount > 5 && !state.openWindowMessageCooldown) {
    state.openWindowMessageCooldown = true;
    store.dispatch('globalSnackbarStore/showSnackbarWithMessage', {
      message: i18n.t('Components.sentences.performanceWarning'),
      type: 'warning',
      timeout: 5000,
      persistent: true
    });
  
  setTimeout(() => {
      state.openWindowMessageCooldown = false;
    }, 5000);
  }
}

function pruneExcessCleanWindows(state) {
  const allCleanWindows = state.windows.flatMap(({ type, windows }) => 
    windows
      .filter(window => window.data?.isDirty !== true)
      .map(window => ({ 
        type, 
        id: window.id, 
        lastEntry: window.data?.lastEntry || Date.now() 
      }))
  );
  
  const windowsMaxSize = 5;
  const sortedCleanWindows = allCleanWindows.sort((a, b) => (a.lastEntry > b.lastEntry) ? 1 : -1);
  
  if (sortedCleanWindows.length > windowsMaxSize) {
    const windowsToRemove = sortedCleanWindows.slice(0, sortedCleanWindows.length-windowsMaxSize);
    
    windowsToRemove.forEach(({ type, id }) => {
      const typeIndex = state.windows.findIndex(windowItem => windowItem.type === type);
      if (typeIndex === -1) return;
      
      const windowIndex = state.windows[typeIndex].windows.findIndex(window => window.id == id);
      if (windowIndex === -1) return;
      
      state.windows[typeIndex].windows.splice(windowIndex, 1);
      
      if (!state.windows[typeIndex].windows.length) {
        state.windows.splice(typeIndex, 1);
      }
    });
  }
}

export default {

  namespaced: true,
  state: {
    windows: [],
    openWindowMessageCooldown: false,
  },
  mutations: {
    addWindow(state, { windowIdentifier, customLink }) {
      var typeObject = state.windows.find(windowItem => windowItem.type === windowIdentifier.type)

      if (typeObject == undefined)
        state.windows.push({ type: windowIdentifier.type, windows: [{ id: windowIdentifier.id, data: { customLink: customLink } }] })
      else {
        var window = typeObject.windows.find(window => window.id == windowIdentifier.id)

        if (window == undefined)
          typeObject.windows.push({ id: windowIdentifier.id, data: { customLink: customLink } });
      }
      pruneExcessCleanWindows(state);
    },

    addRoutingHistory(state, dto) {
      const window = getWindow(state, dto.type, dto.id);
      if (window === undefined) { return; }

      Vue.set(window.data, 'routingHistory', dto.routingHistory);
    },

    addPicture(state, dto) {
      const window = getWindow(state, dto.type, dto.id);
      if (window === undefined) { return; }

      Vue.set(window.data, 'pic', dto.canvas);
    },

    setWindowDirty(state, dto) {
      const window = getWindow(state, dto.type, dto.id);
      if (window === undefined) { return; }

      if(window.data?.isDirty && dto.isDirty) 
        return;

      Vue.set(window.data, 'isDirty', dto.isDirty);

      if(dto.isDirty)
        checkDirtyWindows(state);
    },

    setWindowLastEntry(state, dto) {
      const window = getWindow(state, dto.type, dto.id);
      if (window === undefined) { return; }

      Vue.set(window.data, 'lastEntry', dto.lastEntry);
    },

    removeWindow(state, dto) {
      var typeIndex = state.windows.findIndex(windowItem => windowItem.type === dto.type)
      if (typeIndex === -1)
        return

      var windowIndex = state.windows[typeIndex].windows.findIndex(window => window.id == dto.id)
      if (windowIndex === -1)
        return

      state.windows[typeIndex].windows.splice(windowIndex, 1)

      if (!state.windows[typeIndex].windows.length)
        state.windows.splice(typeIndex, 1)
    },

    // DO NOT USE FOR 'persistentData' FIELDS!!!!
    setWindowDataField(state, { type, id, targetFieldPath, value }) { 
      const properties = targetFieldPath.split('.');
      const lastIndex = properties.length - 1;

      const window = getWindow(state, type, id);
      if (window === undefined) { return; }

      let currentProp = window.data;

      for (let propIndex = 0; propIndex < lastIndex; propIndex++) {
        var propName = properties[propIndex];
        if (!currentProp.hasOwnProperty(propName) || currentProp[propName] == undefined) Vue.set(currentProp, propName, {})
        currentProp = currentProp[propName];
      }

      var lastProperty = properties[lastIndex];
      if (!currentProp.hasOwnProperty(lastProperty) || Array.isArray(currentProp)) 
        Vue.set(currentProp, lastProperty, value);
      else currentProp[lastProperty] = value;
    },

    setWindowDataProp(state, { type, id, targetFieldPath, value }) {
      const properties = targetFieldPath.split('.');
      const lastIndex = properties.length - 1;

      const window = getWindow(state, type, id);
      if (window === undefined) { return; }

      let currentProp = window.data;

      for (let propIndex = 0; propIndex < lastIndex; propIndex++) {
        var propName = properties[propIndex];
        if (!currentProp.hasOwnProperty(propName) || currentProp[propName] == undefined) Vue.set(currentProp, propName, {})
        currentProp = currentProp[propName];
      }

      var lastProperty = properties[lastIndex];
      if (!currentProp.hasOwnProperty(lastProperty) || Array.isArray(currentProp)) Vue.set(currentProp, lastProperty, value)
      else currentProp[lastProperty] = value;
    },

    setDataFieldForAllWindows(state, { targetFieldPath, value }) {
      const properties = targetFieldPath.split('.');
      const lastIndex = properties.length - 1;

      let windows = [];
      state.windows.forEach(window => windows = [...windows, ...window.windows]);
      if (windows === undefined) { return; }

      windows.forEach(window => {
        let currentProp = window.data;

        for (let propIndex = 0; propIndex < lastIndex; propIndex++) {
          let propName = properties[propIndex];
          if (!currentProp.hasOwnProperty(propName) || currentProp[propName] == undefined) Vue.set(currentProp, propName, {})
          currentProp = currentProp[propName];
        }

        let lastProperty = properties[lastIndex];
        if (!currentProp.hasOwnProperty(lastProperty) || Array.isArray(currentProp)) Vue.set(currentProp, lastProperty, value)
        else currentProp[lastProperty] = value;
      });
      checkDirtyWindows(state);
    },

    removeWindowDataField(state, { type, id, targetFieldPath }) {
      const window = getWindow(state, type, id);
      if (window === undefined) { return; }

      const properties = targetFieldPath.split('.');
      const lastProperty = properties.splice(properties.length - 1, 1);

      const currentProp = get(window.data, properties.join('.'));
      currentProp.splice(lastProperty, 1);
    },

    setWindowLabel(state, { type, id, label }) {
      const window = getWindow(state, type, id);
      if (window === undefined) { return; }

      Vue.set(window.data, 'label', label);
    },
    setWindowHtmlLabel(state, { type, id, htmlLabel }) {
      const window = getWindow(state, type, id);
      if (window === undefined) { return; }

      Vue.set(window.data, 'htmlLabel', htmlLabel);
    },
    resetStore(state) {
      Vue.set(state, 'windows', []);
    }
  },
  actions: {
    removeWindowAndDeleteTableReferences({ state, commit }, dto) {
      const oldRoutingHistory = getWindow(state, dto.type, dto.id)?.data?.routingHistory;

      if (oldRoutingHistory !== undefined) {
        const tableCaches = oldRoutingHistory.map(_item => {
          return { ..._item.to.query, ..._item.from.query }
        }).filter((_query) => _query['table-cache-id'] !== undefined)
          .map((_tableIdentifier) => _tableIdentifier['table-cache-id']);
        commit('tableStore/deleteTableReferences', tableCaches, { root: true });
      }

      commit('removeWindow', dto);
    },
    async setWindowDataField({ commit }, { type, id, targetFieldPath, value }) {
      commit('setWindowDataField', { type, id, targetFieldPath, value });
    },
    resetStore({ commit }) {
      commit('resetStore');
    }
  },
  getters: {
    getActiveWindows: state => state.windows.flatMap(
      ({ type, windows }) => windows.map(({ id, data: { pic, label, htmlLabel, isDirty, customLink, lastEntry } }) =>
        ({ type, id, pic, label, htmlLabel, isDirty, customLink, lastEntry }))),

    getWindowData: (state) => ({ type, id }) => getWindow(state, type, id)?.data,

    isActiveWindow: (state) => ({ type, id }) => getWindow(state, type, id) != undefined,

    getRoutingHistory: state => {
      const routingHistory = state.windows.map((_window) => {
        return _window.windows.map((windowData) => {
          return windowData?.data?.routingHistory ?? [];
        })
      }).flat().filter((_routingHistory) => _routingHistory.length >= 1);

      return routingHistory;
    }
  },
}
